<script lang="ts" setup>
  import checkIcon from '@assets/icons/check.svg'
  import type { Subscription } from '@/types'
  import { ref } from 'vue'
  import { SUBSCRIPTION_STATUS } from '@/enum/payments.ts'
  import { useDate } from 'vuetify'
  import CancelSubscription from './CancelSubscription.vue'

  const props = defineProps<{
    subscription?: Subscription
  }>()

  const emit = defineEmits<{
    close: []
  }>()

  const route = useRoute('profile-subscriptions-id')
  const error = ref('')
  const subscriptionStore = useSubscriptionsStore()
  const subscriptionData = ref<Subscription | null>(props.subscription || null)
  const loading = ref(false)
  const showCancelView = ref(false)
  const date = useDate()
  const { t } = useI18n()

  const isActiveSubscription = computed(() =>
    subscriptionData.value?.id === subscriptionStore?.subscription?.id &&
    subscriptionStore?.subscription?.subscription_status_id === SUBSCRIPTION_STATUS.active,
  )

  const fetchSubscription = async () => {
    loading.value = true
    try {
      const { data } = await subscriptionStore.fetchSubscriptions({
        filter: {
          id: parseInt(route.params.id),
        },
        include: 'user,plan,planPaymentMethod',
      })

      if (data.data.length > 0) {
        subscriptionData.value = data.data[0]
      } else {
        subscriptionData.value = null
      }
    } catch (err) {
      console.error('Error fetching subscription:', err)
      error.value = t('subscription.error_fetching_subscription')
    } finally {
      loading.value = false
    }
  }


  const subscriptionBenefits = reactive([
    'subscription.benefits.one',
    'subscription.benefits.two',
    'subscription.benefits.three',
  ])

  const endedAtTitle = computed(() => {
    if (subscriptionData.value?.plan.is_recurring) return t('subscription.details.end_at_recurring')
    return t('subscription.details.end_at')
  })

  const endedAtDate = computed(() => {
    if (subscriptionData.value?.plan.is_recurring) return date.format(subscriptionData.value.next_payment, 'keyboardDate')
    return date.format(subscriptionData.value?.ended_at, 'keyboardDate')
  })

  onMounted(() => {
    // Only fetch if not in dialog mode (no prop subscription provided)
    if (!props.subscription) {
      fetchSubscription()
    }
  })

  // Watch for prop changes when used in dialog mode
  watch(
    () => props.subscription,
    newSubscription => {
      if (newSubscription) {
        subscriptionData.value = newSubscription
      }
    },
    { immediate: true }
  )

  const handleCancelClick = () => {
    showCancelView.value = true
  }

  const handleCancelBack = () => {
    showCancelView.value = false
  }
</script>

<template>
  <v-container
    class="pa-0 subscription-details bg-login-bg"
    :fluid="$vuetify.display.smAndDown"
    max-width="420"
  >
    <!-- Show Cancel Subscription View -->
    <template v-if="showCancelView">
      <cancel-subscription
        @back="handleCancelBack"
      />
    </template>

    <!-- Show Subscription Details View -->
    <template v-else>
      <div>
        <!-- Header -->
        <div class="d-flex align-center mb-4">
          <v-btn
            icon="mdi-arrow-left"
            size="small"
            variant="text"
            @click="emit('close')"
          />
          <h2 class="text-h6 ms-2">{{ t('subscription.details.title') }}</h2>
        </div>
        <!-- Error Alert -->
        <v-alert
          v-if="error"
          class="mb-4"
          closable
          type="error"
          @click:close="error = ''"
        >
          {{ error }}
        </v-alert>

        <!-- Loading Skeleton -->
        <template v-if="loading">
          <v-skeleton-loader
            class="mx-auto bg-grey-800 rounded-md"
            height="450"
            max-width="540"
            type="card"
          >
            <template #default>
              <div class="px-3 py-4">
                <!-- Price Section Skeleton -->
                <div class="pa-3 text-center">
                  <!-- Large price skeleton -->
                  <div class="d-flex align-center justify-center ga-2 mb-2">
                    <v-skeleton-loader
                      height="48"
                      type="heading"
                      width="120"
                    />
                    <v-skeleton-loader
                      height="24"
                      type="text"
                      width="60"
                    />
                  </div>
                  <!-- Plan type subtitle skeleton -->
                  <v-skeleton-loader
                    class="mx-auto mb-4"
                    height="16"
                    type="text"
                    width="180"
                  />
                </div>

                <!-- Benefits List Skeleton -->
                <div class="mt-2">
                  <v-skeleton-loader
                    v-for="n in 3"
                    :key="n"
                    class="mb-3"
                    height="28"
                    type="list-item-avatar"
                  >
                    <template #default>
                      <div class="d-flex align-center ga-2 pa-0">
                        <!-- Check icon skeleton -->
                        <v-skeleton-loader
                          height="20"
                          type="avatar"
                          width="20"
                        />
                        <!-- Benefit text skeleton -->
                        <v-skeleton-loader
                          height="16"
                          type="text"
                          :width="n === 1 ? '200' : n === 2 ? '170' : '220'"
                        />
                      </div>
                    </template>
                  </v-skeleton-loader>
                </div>

                <!-- Cancel Button Section Skeleton (conditional) -->
                <template v-if="isActiveSubscription">
                  <v-divider class="my-4" color="grey-600" />
                  <v-skeleton-loader
                    height="40"
                    type="button"
                    width="100%"
                  />
                </template>
              </div>
            </template>
          </v-skeleton-loader>
        </template>

        <!-- Loaded Content -->
        <v-card
          v-else-if="subscriptionData && !loading"
          class="mx-auto px-3 py-4 bg-grey-800"
          flat
          rounded="md"
        >
          <v-card-text>
            <div class="pa-3">
              <div class="font-weight-bold text-grey-100 text-center price-details">
                <span class="font-weight-bold text-h3 text-h2-app">{{ subscriptionData?.price_local }}</span>
                <span class="font-weight-medium ms-2 text-medium text-lg-h5">
                  {{
                    t('plan_price_details', {
                      price: t(`subscription.currencies.${subscriptionData?.currency}`),
                      duration: t(`subscription.${subscriptionData?.plan?.is_recurring ? 'is_recurring' : 'not_recurring'}.durations.${subscriptionData?.plan.duration}`),
                    })
                  }}
                </span>
              </div>

              <p class="text-small text-grey-darken-1 text-center text-grey-200">
                <i18n-t
                  :keypath="subscriptionData?.plan?.is_recurring ? 'subscription.details.monthly_plan' : 'subscription.details._time_plan'"
                />
              </p>
            </div>
            <v-row v-if="subscriptionData.consultation_total > 0" class="mt-6 mb-4 ga-11 text-center" no-gutters>
              <v-col>
                <h5 class="text-caption font-weight-medium mb-2 text-grey-100">
                  {{ t('subscription.details.consultation_amount_title') }}</h5>
                <p class="text-caption font-weight-bold text-grey-100">{{
                  t('subscription.details.remaining_consultations', {
                    total: subscriptionData.consultation_total,
                    remaining: Math.max(subscriptionData.consultation_total - subscriptionData.consultation_used, 0)
                  })
                }}</p>
              </v-col>
              <v-col>
                <h5 class="text-caption font-weight-medium mb-2 text-grey-100">{{ endedAtTitle }}</h5>
                <p class="text-caption font-weight-bold text-grey-100">{{ endedAtDate }}</p>
              </v-col>
              <v-col>
                <h5 class="text-caption font-weight-medium mb-2 text-grey-100">
                  {{ t('subscription.details.subscription_name') }}</h5>
                <p class="text-caption font-weight-bold text-grey-100">{{ subscriptionData.plan.name }}</p>
              </v-col>
            </v-row>

            <v-list
              class="mt-2"
              density="compact"
              lines="one"
              slim
              style="background-color: inherit"
            >
              <v-list-item
                v-for="subscriptionBenefit in subscriptionBenefits"
                :key="subscriptionBenefit"
                class="mb-3 pa-0"
                density="compact"
                min-height="auto"
                slim
              >
                <div class="d-flex align-center ga-2">
                  <v-icon :icon="checkIcon" />
                  <v-list-item-title class="text-subtitle-2 font-weight-medium text-grey-100">
                    <i18n-t :keypath="subscriptionBenefit" />
                  </v-list-item-title>
                </div>
              </v-list-item>
            </v-list>
          </v-card-text>

          <div v-if="isActiveSubscription">
            <v-divider color="grey-600" thickness="1" />
            <v-card-actions>
              <v-btn
                block
                class="text-caption font-weight-medium"
                color="grey-200"
                :ripple="false"
                slim
                text
                @click="handleCancelClick"
              >
                {{ t('subscription.details.cancel_subscription') }}
              </v-btn>
            </v-card-actions>
          </div>
        </v-card>

        <!-- Empty State (when no subscription found) -->
        <div v-else-if="!subscriptionData && !loading" class="text-center py-8">
          <v-icon
            color="grey-400"
            icon="mdi-alert-circle-outline"
            size="64"
          />
          <h3 class="text-h6 mt-4 text-grey-600">{{ t('subscription.not_found') }}</h3>
          <p class="text-body-2 text-grey-500 mt-2">{{ t('subscription.not_found_description') }}</p>
        </div>
      </div>
    </template>
  </v-container>
</template>

<style lang="scss" scoped>
.subscription-details :deep(.v-skeleton-loader) {
  overflow: hidden;
  display: flex;
  align-items: center;
  align-content: center;

  .v-skeleton-loader__bone {
    height: 50%;
  }
}
</style>
